import { HttpEventType } from '@angular/common/http';
import { Component, computed, inject, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  AbilityService,
  FileUploadService,
  NgbModalService,
  SpinnerService,
  DocPreviewComponent,
  SvgIconsComponent,
  BtnLoaderDirective
} from '@zayan-erp/shared';
import { CustomDocumentDraft, CustomDocumentSigner } from '../../../../models/custom-documment.models';
import { SalesApsService } from '../../../../services/sales-aps.service';
import { ToastrService } from 'ngx-toastr';
import { filter, map, merge, switchMap, take, tap } from 'rxjs';
import { DealDocumentsService } from '@zayan-erp/sales';

@Component({
  selector: 'lib-custom-document',
  imports: [DocPreviewComponent, SvgIconsComponent, BtnLoaderDirective],
  templateUrl: './custom-document.html',
  styleUrl: './custom-document.scss'
})
export class CustomDocument {
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #toastrService = inject(ToastrService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #dealDocumentsService = inject(DealDocumentsService);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  readonly generateDocumentButtonId = 'generate-document-custom';

  dealId = input.required<string>();
  sequenceNumber = input<number>(1);

  refreshRequested = output<void>();
  documentGenerated = output<void>();

  signersModal = viewChild<TemplateRef<unknown>>('signersModal');
  documentPreviewModal = viewChild<TemplateRef<unknown>>('documentPreviewModal');

  uploadedDocument = signal<CustomDocumentDraft | null>(null);
  previewDocument = signal<{ documentName: string; directUrl: string } | null>(null);
  draggedSignerIndex = signal<number | null>(null);

  hasUploadedDocument = computed(() => this.uploadedDocument() !== null);
  canGenerate = computed(
    () => Boolean(this.uploadedDocument()?.documentId) && (this.uploadedDocument()?.signers.length ?? 0) > 0
  );

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadDocument(file);
      input.value = '';
    }
  }

  uploadDocument(file: File): void {
    if (!this.canWriteDeals()) return;
    if (!file) {
      this.#toastrService.warning('Please select a file to upload');
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['doc', 'docx'].includes(extension)) {
      this.#toastrService.warning('Only DOC, and DOCX files are allowed');
      return;
    }

    const dealId = this.dealId();
    this.#spinnerService.handleSpinnerStart();
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3(awsData).pipe(
            filter((uploadEvent: any) => uploadEvent?.type === HttpEventType.Response),
            take(1),
            map(() => awsData)
          )
        ),
        switchMap(awsData =>
          this.#fileUploadService
            .getPresignedUrlFromS3Key(awsData.s3Key, file.name)
            .pipe(map(directUrl => ({ ...awsData, directUrl })))
        ),
        tap(awsData => {
          this.uploadedDocument.set({
            documentId: awsData.documentId,
            documentName: file.name,
            s3Key: awsData.s3Key,
            directUrl: awsData.directUrl,
            signers: []
          });
        }),
        switchMap(awsData =>
          this.#dealDocumentsService.verifyCustomDocument(dealId, awsData.documentId, {
            showLoader: false,
            showMessage: false
          })
        )
      )
      .subscribe({
        next: signers => this.applySignersAndOpenModal(signers ?? []),
        error: () => this.#spinnerService.handleSpinnerStop()
      })
      .add(() => this.#spinnerService.handleSpinnerStop());
  }

  removeUploadedDocument(): void {
    if (!this.canWriteDeals()) return;
    this.uploadedDocument.set(null);
  }

  openUploadedDocumentPreview(): void {
    const uploaded = this.uploadedDocument();
    if (!uploaded) {
      return;
    }

    if (uploaded.directUrl) {
      this.openDocumentPreviewModal(uploaded.documentName, uploaded.directUrl);
      return;
    }

    this.#spinnerService.handleSpinnerStart();
    this.#salesApsService
      .getDocumentFileUrl(uploaded.documentId)
      .subscribe({
        next: fileUrl => {
          if (!fileUrl) {
            this.#toastrService.error('Document preview is unavailable');
            return;
          }
          this.uploadedDocument.set({ ...uploaded, directUrl: fileUrl });
          this.openDocumentPreviewModal(uploaded.documentName, fileUrl);
        },
        error: () => this.#toastrService.error('Document preview is unavailable')
      })
      .add(() => this.#spinnerService.handleSpinnerStop());
  }

  generateCustomDocument(): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.dealId();
    const uploaded = this.uploadedDocument();
    if (!dealId || !uploaded?.documentId) {
      this.#toastrService.warning('Please upload a document before generating');
      return;
    }

    const signers = uploaded.signers.map(signer => ({
      entityType: signer.entityType,
      sequence: signer.defaultSequence
    }));

    this.#dealDocumentsService
      .generateCustomDocument(
        dealId,
        {
          s3DocumentId: uploaded.documentId,
          signers,
          signingType: 0
        },
        this.generateDocumentButtonId
      )
      .subscribe({
        next: () => {
          this.#modalService.dismissAll();
          this.documentGenerated.emit();
          this.refreshRequested.emit();
          this.uploadedDocument.set(null);
        }
      });
  }

  formatSigneeType(entityType: string): string {
    return this.#salesApsService.formatSigneeType(entityType);
  }

  onSignerDragStart(index: number): void {
    this.draggedSignerIndex.set(index);
  }

  onSignerDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onSignerDrop(targetIndex: number): void {
    const uploaded = this.uploadedDocument();
    const sourceIndex = this.draggedSignerIndex();
    this.draggedSignerIndex.set(null);

    if (!uploaded || sourceIndex === null || sourceIndex === targetIndex) {
      return;
    }

    const lastIndex = uploaded.signers.length - 1;
    if (sourceIndex === lastIndex || targetIndex === lastIndex) {
      return;
    }

    const signers = [...uploaded.signers];
    const [movedSigner] = signers.splice(sourceIndex, 1);
    signers.splice(targetIndex, 0, movedSigner);

    this.uploadedDocument.set({
      ...uploaded,
      signers: signers.map((signer, index) => ({ ...signer, defaultSequence: index + 1 }))
    });
  }

  private applySignersAndOpenModal(signers: CustomDocumentSigner[]): void {
    const uploaded = this.uploadedDocument();
    if (!uploaded?.directUrl) {
      this.#toastrService.error('Document preview is unavailable');
      return;
    }

    const orderedSigners = signers.map((signer, index) => ({ ...signer, defaultSequence: index + 1 }));

    this.uploadedDocument.set({
      ...uploaded,
      signers: orderedSigners
    });
    this.openSignersModal();
  }

  private openSignersModal(): void {
    const modal = this.signersModal();
    if (!modal) {
      return;
    }
    this.#modalService.open(modal, this.#ngbModalService.fullscreenModal);
  }

  private openDocumentPreviewModal(documentName: string, directUrl: string): void {
    const modal = this.documentPreviewModal();
    if (!modal) {
      return;
    }

    this.previewDocument.set({ documentName, directUrl });
    const modalRef = this.#modalService.open(modal, this.#ngbModalService.fullscreenModal);
    merge(modalRef.closed, modalRef.dismissed).subscribe(() => {
      this.previewDocument.set(null);
    });
  }
}
